/**
 * free-gift-engine.js — V1
 * Auto-adds/removes gift line items based on cart state and theme settings.
 * Subscribes to PUB_SUB_EVENTS.cartUpdate (theme's existing PubSub).
 */
(function () {
  'use strict';

  const SOURCE_TAG = 'free-gift-engine';
  // Debounce kept short so the gap between cart-items's render (with stale gift
  // state) and our reconcile is barely perceptible — was 200ms, dropped to 60ms
  // because cart-items's own pipeline already finished by then in practice.
  const RECONCILE_DEBOUNCE_MS = 60;
  const RETRY_BACKOFF_MS = 1000;

  class FreeGiftEngine {
    constructor() {
      this.rules = [];
      this.rejectedRules = [];
      this.busy = false;
      this._pending = false;
      this.evalTimer = null;
      this.cartUpdateUnsubscribe = null;
      this.lastFailedAddAt = 0;
      // Monotonic render version. Each refreshCartUIs increments before fetching
      // sections; if a slower fetch finishes after a newer one already wrote the
      // DOM, the stale fetch's writeback is aborted (last-correct-write wins,
      // not last-write-arrives).
      this._renderVersion = 0;
      // Cache last-rendered cart token to skip redundant section refreshes when
      // nothing actually changed (avoids flicker on rapid bursts of events).
      this._lastRenderedToken = null;
    }

    loadConfig() {
      const node = document.getElementById('free-gift-rules-config');
      if (!node) {
        console.warn('[free-gift] config node not found — engine inactive');
        return false;
      }
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        const rules = Array.isArray(parsed.rules) ? parsed.rules : [];
        // Validate with rejection logging so admins can see WHY a rule is ignored.
        // Bug 1 fix: regras desativadas (enabled !== true) são IGNORADAS SILENCIOSAMENTE.
        // O admin escolheu desativar — não é "configuração pendente", é estado intencional.
        // Apenas regras enabled=true mas mal configuradas vão para rejectedRules (ex: produto
        // não selecionado, threshold inválido, modo desconhecido). Assim o banner amarelo
        // de admin só mostra problemas reais que impedem a regra de funcionar.
        const validRules = [];
        const rejected = [];
        rules.forEach((r) => {
          if (!r || r.enabled !== true) return; // skip disabled rules silently
          const reason = this.invalidReason(r);
          if (reason) {
            rejected.push({ rule_id: r.id, name: r.name || '', reason });
          } else {
            validRules.push(r);
          }
        });
        this.rules = validRules;
        this.rejectedRules = rejected;
        this.ui = parsed.ui || {};
        if (rejected.length) {
          rejected.forEach((rj) => {
            console.warn(
              '[free-gift] rule #' + rj.rule_id + ' (' + (rj.name || 'sem nome') + ') ignorada: ' + rj.reason
            );
          });
        }
        if (validRules.length === 0) {
          console.warn('[free-gift] no valid rules after validation — engine inactive');
        }
        return validRules.length > 0;
      } catch (err) {
        console.error('[free-gift] failed to parse config', err);
        return false;
      }
    }

    /**
     * Returns a string describing why the rule is invalid, or '' when valid.
     * Replaces isValidRule for explicit diagnostics (Bug 3).
     */
    invalidReason(rule) {
      if (!rule) return 'rule is null/undefined';
      if (rule.enabled !== true) return 'disabled (enabled = false)';
      const mode = rule.gift_mode || 'auto';
      const qty = parseInt(rule.gift_quantity, 10);
      if (!Number.isFinite(qty) || qty < 1) return 'invalid gift_quantity (' + rule.gift_quantity + ')';
      if (mode === 'auto') {
        const variantId = parseInt(rule.gift_variant_id, 10);
        const hasFixedVariant = Number.isFinite(variantId) && variantId > 0;
        const hasInferableVariants =
          Array.isArray(rule.gift_variants) &&
          rule.gift_variants.some((v) => v && v.available);
        if (!hasFixedVariant && !hasInferableVariants) {
          return '"auto" mode requires gift_variant_id OR a product with available variants (current gift_variant_id: "' + (rule.gift_variant_id || '') + '")';
        }
      } else if (mode === 'choose') {
        if (!rule.gift_product_id) return '"choose" mode requires gift_product (no product selected)';
        if (!Array.isArray(rule.gift_variants) || rule.gift_variants.length === 0) {
          return '"choose" mode requires a product with available variants';
        }
      } else {
        return 'unknown gift_mode: "' + mode + '"';
      }
      const validTriggers = ['cart_total', 'item_count', 'product_in_cart'];
      if (!validTriggers.includes(rule.trigger_type)) return 'invalid trigger_type: "' + rule.trigger_type + '"';
      if (rule.trigger_type === 'product_in_cart' && !rule.trigger_product_id) {
        return '"product_in_cart" trigger requires trigger_product selected';
      }
      if (rule.trigger_type !== 'product_in_cart') {
        const threshold = parseFloat(rule.threshold_value);
        if (!Number.isFinite(threshold) || threshold < 0) {
          return 'invalid threshold_value: "' + rule.threshold_value + '"';
        }
      }
      return '';
    }

    isValidRule(rule) {
      // Kept for back-compat / external callers (e.g. existing tests).
      return this.invalidReason(rule) === '';
    }

    isGift(item) {
      return item && item.properties && item.properties._is_gift === 'true';
    }

    /**
     * Infers the best gift variant by sampling the customer's non-gift cart items,
     * extracting size tokens (PP, P, M, G, GG, XGG, XS, S, L, XL, XXL, numeric 34-48,
     * and "Único"), counting weighted by quantity, and matching against the rule's
     * available gift variants. Falls back to first-available variant when no match.
     * Used when gift_variant_id is left blank in admin (auto mode).
     */
    inferVariantFromCart(rule, cart) {
      const variants = (rule.gift_variants || []).filter((v) => v && v.available);
      if (variants.length === 0) return null;
      if (variants.length === 1) return parseInt(variants[0].id, 10);

      const SIZE_TOKENS = [
        'XGG', 'GG', 'XG', 'XL', 'XXL', 'XXXL',
        'PP', 'XS', 'S', 'M', 'P', 'G', 'L',
      ];
      const NUMERIC_RE = /\b(3[2-9]|4[0-9]|5[0-6])\b/;

      const extractToken = (str) => {
        if (!str) return null;
        const upper = String(str).toUpperCase();
        for (const tok of SIZE_TOKENS) {
          const re = new RegExp('(?:^|[^A-Z0-9])' + tok + '(?:$|[^A-Z0-9])');
          if (re.test(upper)) return tok;
        }
        const num = upper.match(NUMERIC_RE);
        if (num) return num[1];
        if (/\b(UNICO|ÚNICO|UNIVERSAL|TAMANHO ÚNICO)\b/.test(upper)) return 'UNICO';
        return null;
      };

      const counts = new Map();
      const nonGiftItems = cart.items.filter((i) => !this.isGift(i));
      nonGiftItems.forEach((it) => {
        const candidates = [];
        candidates.push(it.variant_title);
        if (Array.isArray(it.options)) candidates.push(it.options.join(' '));
        if (Array.isArray(it.options_with_values)) {
          it.options_with_values.forEach((ov) => {
            if (ov && (ov.name || '').toLowerCase().match(/tamanho|size/)) {
              candidates.push(ov.value);
            } else if (ov) {
              candidates.push(ov.value);
            }
          });
        }
        for (const c of candidates) {
          const tok = extractToken(c);
          if (tok) {
            counts.set(tok, (counts.get(tok) || 0) + (it.quantity || 1));
            break;
          }
        }
      });

      if (counts.size === 0) {
        return parseInt(variants[0].id, 10);
      }

      const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
      for (const [token] of sorted) {
        const match = variants.find((v) => {
          const vTok = extractToken(v.title);
          return vTok === token;
        });
        if (match) return parseInt(match.id, 10);
      }

      return parseInt(variants[0].id, 10);
    }

    ruleMatches(rule, cart) {
      const nonGiftItems = cart.items.filter((i) => !this.isGift(i));
      if (rule.trigger_type === 'cart_total') {
        const subtotalCents = nonGiftItems.reduce(
          (sum, i) => sum + i.final_line_price,
          0
        );
        return subtotalCents >= parseFloat(rule.threshold_value);
      }
      if (rule.trigger_type === 'item_count') {
        const totalQty = nonGiftItems.reduce((sum, i) => sum + i.quantity, 0);
        return totalQty >= parseFloat(rule.threshold_value);
      }
      if (rule.trigger_type === 'product_in_cart') {
        const triggerId = parseInt(rule.trigger_product_id, 10);
        return nonGiftItems.some((i) => i.product_id === triggerId);
      }
      return false;
    }

    evaluate(cart) {
      const to_add = [];
      const to_remove = [];
      const pending_choices = [];
      const giftLines = cart.items.filter((i) => this.isGift(i));
      const expectedGiftRuleIds = new Set();

      this.rules.forEach((rule) => {
        const qty = parseInt(rule.gift_quantity, 10) || 1;
        const ruleId = String(rule.id);
        const mode = rule.gift_mode || 'auto';
        const matches = this.ruleMatches(rule, cart);

        // Find any gift line for this rule (regardless of variant — choose mode varies).
        const present = giftLines.find(
          (i) => i.properties._gift_rule_id === ruleId
        );

        if (matches) {
          expectedGiftRuleIds.add(ruleId);

          if (mode === 'auto') {
            const fixedVariantId = parseInt(rule.gift_variant_id, 10);
            const hasFixedVariant = Number.isFinite(fixedVariantId) && fixedVariantId > 0;
            const variantId = hasFixedVariant
              ? fixedVariantId
              : this.inferVariantFromCart(rule, cart);

            if (!variantId) {
              // Could not infer (no available variants) — skip silently.
              return;
            }

            if (!present) {
              to_add.push({ variant_id: variantId, quantity: qty, rule_id: ruleId });
            } else if (hasFixedVariant && present.variant_id !== variantId) {
              // Fixed variant in settings changed — replace.
              // For inferred mode, never swap once added (avoids jarring UX
              // when customer adds another item that shifts the inferred size).
              to_remove.push(present.key);
              to_add.push({ variant_id: variantId, quantity: qty, rule_id: ruleId });
            } else if (present.quantity !== qty) {
              to_add.push({
                variant_id: present.variant_id,
                quantity: qty,
                rule_id: ruleId,
                update_key: present.key,
              });
            }
          } else if (mode === 'choose') {
            if (!present) {
              pending_choices.push({
                rule_id: ruleId,
                product_id: rule.gift_product_id,
                product_title: rule.gift_product_title,
                product_handle: rule.gift_product_handle,
                product_image: rule.gift_product_image,
                product_price: rule.gift_product_price,
                product_compare_at_price: rule.gift_product_compare_at_price,
                variants: rule.gift_variants || [],
                quantity: qty,
              });
            }
            // If present, leave alone — user already picked.
          }
        }
      });

      giftLines.forEach((g) => {
        const ruleId = g.properties._gift_rule_id || '';
        if (!expectedGiftRuleIds.has(ruleId)) {
          to_remove.push(g.key);
        }
      });

      this.pendingChoices = pending_choices;
      return { to_add, to_remove, pending_choices };
    }

    async fetchCart() {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('fetch cart failed: ' + res.status);
      return res.json();
    }

    /**
     * Fetches the latest server-rendered HTML for the cart sections. Used as the
     * authoritative re-render source after reconcile, even when no mutation happened
     * — defeats the race where cart-items.js renders stale section HTML *after* our
     * removeGift, causing a phantom gift to reappear in the DOM (cart.js says it's
     * gone, but DOM still shows it). Always returning a fresh sections payload
     * ensures the visible state matches Shopify's truth.
     */
    async fetchSections() {
      const sectionNames = this.getCartSectionsToRender().map((s) => s.section).join(',');
      const res = await fetch('/?sections=' + encodeURIComponent(sectionNames), {
        credentials: 'same-origin',
        headers: { 'X-Free-Gift-Source': SOURCE_TAG },
      });
      if (!res.ok) return null;
      const sections = await res.json();
      return { sections };
    }

    /**
     * Returns the section list to request inline in /cart/* responses, mirroring
     * the theme's CartDrawerItems.getSectionsToRender(). When the cart-drawer-items
     * element is on the page, we ask it directly so we stay in sync with theme
     * customizations. Falls back to defaults otherwise.
     */
    getCartSectionsToRender() {
      const items = document.querySelector('cart-drawer-items');
      if (items && typeof items.getSectionsToRender === 'function') {
        try {
          return items.getSectionsToRender();
        } catch (_) {}
      }
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    async removeGift(lineKey) {
      const sections = this.getCartSectionsToRender().map((s) => s.section);
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Free-Gift-Source': SOURCE_TAG },
        body: JSON.stringify({ id: lineKey, quantity: 0, sections, sections_url: window.location.pathname }),
      });
      if (!res.ok) throw new Error('remove gift failed: ' + res.status);
      return res.json();
    }

    async addGift(variantId, quantity, ruleId) {
      const sections = this.getCartSectionsToRender().map((s) => s.section);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Free-Gift-Source': SOURCE_TAG },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
              properties: {
                _is_gift: 'true',
                _gift_rule_id: ruleId,
              },
            },
          ],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const err = new Error('add gift failed: ' + res.status);
        err.status = res.status;
        err.body = errBody;
        throw err;
      }
      return res.json();
    }

    async updateGiftQty(lineKey, quantity) {
      const sections = this.getCartSectionsToRender().map((s) => s.section);
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Free-Gift-Source': SOURCE_TAG },
        body: JSON.stringify({ id: lineKey, quantity, sections, sections_url: window.location.pathname }),
      });
      if (!res.ok) throw new Error('update gift qty failed: ' + res.status);
      return res.json();
    }

    async applyDiff(diff) {
      let mutated = false;
      let lastResponse = null;

      for (const lineKey of diff.to_remove) {
        try {
          lastResponse = await this.removeGift(lineKey);
          mutated = true;
        } catch (err) {
          console.error('[free-gift] remove failed', lineKey, err);
        }
      }

      for (const op of diff.to_add) {
        try {
          if (op.update_key) {
            lastResponse = await this.updateGiftQty(op.update_key, op.quantity);
          } else {
            lastResponse = await this.addGift(op.variant_id, op.quantity, op.rule_id);
          }
          mutated = true;
        } catch (err) {
          // Bug 3: explicit diagnostic — print HTTP status + Shopify error body
          // so admins know exactly why an add failed (esgotado, variante inexistente, etc.).
          const status = err && err.status ? err.status : 'n/a';
          const body = err && err.body ? err.body : null;
          console.warn(
            '[free-gift] add failed (rule=' + op.rule_id + ', variant=' + op.variant_id +
            ', qty=' + op.quantity + ', status=' + status + ') — retrying once',
            body || err
          );
          if (Date.now() - this.lastFailedAddAt > RETRY_BACKOFF_MS) {
            this.lastFailedAddAt = Date.now();
            await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
            try {
              if (op.update_key) {
                lastResponse = await this.updateGiftQty(op.update_key, op.quantity);
              } else {
                lastResponse = await this.addGift(op.variant_id, op.quantity, op.rule_id);
              }
              mutated = true;
            } catch (err2) {
              const status2 = err2 && err2.status ? err2.status : 'n/a';
              const body2 = err2 && err2.body ? err2.body : null;
              console.error(
                '[free-gift] retry failed — giving up (rule=' + op.rule_id +
                ', variant=' + op.variant_id + ', status=' + status2 + ')',
                body2 || err2
              );
            }
          }
        }
      }

      this.lastResponse = lastResponse;
      return mutated;
    }

    /**
     * Surgical re-render of only the cart parts that depend on cart state.
     * Replaces *individual children* of .drawer__inner instead of wholesale
     * innerHTML — preserves stateful nodes:
     *   - #cd-gift-chooser-mount (modal would flicker / become unclickable)
     *   - .cart-drawer-items-wrapper (cart-upsell MutationObserver re-fetches the
     *     entire page on any subtree change, causing the cross-sell to "blink")
     * Updates only:
     *   - cart-drawer-items innerHTML (line items, qty, form)
     *   - .drawer__footer (totals, discount, parcelamento)
     *   - .cd-progress-section (Faltam R$X bar)
     *   - cart-icon-bubble (badge in header)
     */
    refreshCartSections(responseWithSections) {
      if (!responseWithSections || !responseWithSections.sections) return;
      // Suppress cart-upsell.js's MutationObserver during our DOM writes so it
      // doesn't fire its window.location.href full-page fetch + replace cycle.
      const hadSetter = typeof window.setUpsellProcessing === 'function';
      if (hadSetter) {
        try { window.setUpsellProcessing(true); } catch (_) {}
      }
      try {
        const drawerHtml = responseWithSections.sections['cart-drawer'];
        const bubbleHtml = responseWithSections.sections['cart-icon-bubble'];
        const itemCount = responseWithSections.item_count;
        const becomingEmpty = typeof itemCount === 'number' && itemCount === 0;

        if (drawerHtml) {
          const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
          if (becomingEmpty) {
            // Empty state changes the structure (footer disappears, .drawer__inner-empty
            // appears) — granular replacement can't express that. Wholesale replace is
            // safe here because no chooser modal applies when cart is empty.
            const sourceInner = parsed.querySelector('.drawer__inner');
            const targetInner = document.querySelector('.cart-drawer .drawer__inner');
            if (sourceInner && targetInner) {
              targetInner.innerHTML = sourceInner.innerHTML;
              // QA#2: server-rendered empty drawer doesn't include the chooser
              // mount (Liquid only emits it when cart != empty). When the cart
              // re-fills and a choose-mode rule fires, notifyChooserUI looks
              // for #cd-gift-chooser-mount and silently no-ops. Re-inject it.
              if (!document.getElementById('cd-gift-chooser-mount')) {
                const mount = document.createElement('div');
                mount.id = 'cd-gift-chooser-mount';
                mount.setAttribute('data-fg-mount', '');
                mount.setAttribute('aria-live', 'polite');
                targetInner.insertBefore(mount, targetInner.firstChild);
              }
            }
          } else {
            // Granular replacement for non-empty → non-empty transitions: only the
            // child elements that depend on cart state. Preserves chooser modal,
            // upsell wrapper, and other stateful nodes.
            this._replaceChildContents(parsed, 'cart-drawer-items');
            this._replaceChildContents(parsed, '.drawer__footer');
            this._replaceChildContents(parsed, '.cd-progress-section');
          }
          // Sync is-empty on the outer .drawer wrapper so theme CSS toggles correctly.
          const sourceDrawer = parsed.querySelector('.drawer');
          const targetDrawer = document.querySelector('.cart-drawer .drawer') ||
                               document.querySelector('#drawerMode.drawer');
          if (sourceDrawer && targetDrawer) {
            const wasEmpty = targetDrawer.classList.contains('is-empty');
            const willBeEmpty = sourceDrawer.classList.contains('is-empty');
            if (wasEmpty !== willBeEmpty) {
              targetDrawer.classList.toggle('is-empty', willBeEmpty);
            }
          }
        }

        if (bubbleHtml) {
          const parsedBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const sourceBubble = parsedBubble.querySelector('.shopify-section') ||
                               parsedBubble.querySelector('#cart-icon-bubble');
          const targetBubble = document.getElementById('cart-icon-bubble');
          if (sourceBubble && targetBubble) {
            targetBubble.innerHTML = sourceBubble.innerHTML;
          }
        }
      } catch (err) {
        console.warn('[free-gift] section refresh failed', err);
      }
      // QA#5: toggle is-empty BEFORE re-enabling cart-upsell observer.
      // Previously this ran AFTER rAF, so the class change fired the (already
      // re-enabled) observer and triggered 1 redundant full-page GET per reconcile.
      const itemCount = responseWithSections.item_count;
      if (typeof itemCount === 'number') {
        const drawerWrapper = document.querySelector('cart-drawer');
        if (drawerWrapper) drawerWrapper.classList.toggle('is-empty', itemCount === 0);
        // CYGNUSS 2026-05-06 v16 (fix late upsell render): sync is-empty no
        // <cart-drawer-items> tambem. _replaceChildContents() so substitui
        // innerHTML do elemento, deixando o className antigo intacto. Quando
        // cart vazio -> 1-item, o is-empty persistia e cart-upsell.js:119
        // early-exit no observer seguinte -> upsell so renderizava apos
        // segundo add ou edit qty (cart.js:92 sincronizava o className).
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', itemCount === 0);
      }
      if (hadSetter) {
        // Re-enable observer on next animation frame so any final mutations from
        // our write are coalesced into the suppressed window.
        requestAnimationFrame(() => {
          try { window.setUpsellProcessing(false); } catch (_) {}
        });
      }
    }

    /**
     * Replaces only the innerHTML of a single child selector inside .drawer__inner,
     * picking it from the parsed source HTML. Skipped silently when either side
     * doesn't contain the selector (e.g. empty cart has no .drawer__footer).
     * Intentionally compares innerHTML to avoid no-op writes that would still
     * trigger MutationObservers and downstream flicker.
     */
    _replaceChildContents(parsedDoc, selector) {
      const target = document.querySelector('.cart-drawer .drawer__inner ' + selector) ||
                     document.querySelector('.drawer__inner ' + selector);
      const source = parsedDoc.querySelector('.drawer__inner ' + selector) ||
                     parsedDoc.querySelector(selector);
      if (!target || !source) return;
      const newHTML = source.innerHTML;
      if (target.innerHTML === newHTML) return; // no-op skip
      target.innerHTML = newHTML;
    }

    async refreshCartUIs(cart) {
      // Skip when nothing actually changed since last render — defeats redundant
      // refreshes on rapid event bursts (e.g. user clicks +/+/+/+ in 300ms).
      // Architect#5: token now includes total_discount, updated_at, AND a
      // per-line key+qty fingerprint — catches gift-swap (M→G same price),
      // line-level discount toggle, and clear-cart that re-issues same token.
      let cartToken = null;
      if (cart) {
        const itemsFingerprint = Array.isArray(cart.items)
          ? cart.items.map((i) => (i.key || i.variant_id) + ':' + i.quantity).join('|')
          : '';
        cartToken = [
          cart.token || '',
          cart.item_count,
          cart.total_price,
          cart.total_discount || 0,
          cart.updated_at || '',
          itemsFingerprint,
        ].join('::');
      }
      if (cartToken && cartToken === this._lastRenderedToken && !this._pending) {
        return;
      }
      // Bump version BEFORE the async fetch so any in-flight slower refresh
      // that resolves after us recognizes its writeback is stale and aborts.
      const myVersion = ++this._renderVersion;
      let payload = null;
      try {
        payload = await this.fetchSections();
      } catch (err) {
        console.warn('[free-gift] fetchSections failed', err);
      }
      // Abort if a newer refresh already started (and likely finished).
      if (myVersion !== this._renderVersion) return;
      if (!payload && this.lastResponse && this.lastResponse.sections) {
        payload = this.lastResponse;
      }
      if (payload) {
        if (typeof payload.item_count !== 'number' && cart && typeof cart.item_count === 'number') {
          payload = { ...payload, item_count: cart.item_count };
        }
        this.refreshCartSections(payload);
        if (cartToken) this._lastRenderedToken = cartToken;
      }
      if (window.PUB_SUB_EVENTS && window.publish) {
        try {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: SOURCE_TAG,
            cartData: this.lastResponse || cart,
          });
        } catch (err) {
          console.warn('[free-gift] publish cartUpdate failed', err);
        }
      }
      try {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: SOURCE_TAG, cart: this.lastResponse || cart },
        }));
      } catch (_) {}
    }

    async reconcile() {
      // Race-safe: events arriving while busy are coalesced into a re-run.
      // Why: cart mutations can fire in bursts (add → quantity update → discount apply)
      // and dropping mid-flight events left footer/discount stale until F5.
      if (this.busy) { this._pending = true; return; }
      this.busy = true;
      this._pending = false;
      let finalCart = null;
      try {
        const cart = await this.fetchCart();
        finalCart = cart;
        const diff = this.evaluate(cart);
        // Always notify chooser UI of pending state, even if no mutations.
        this.notifyChooserUI();
        if (diff.to_add.length || diff.to_remove.length) {
          const mutated = await this.applyDiff(diff);
          if (mutated) {
            finalCart = await this.fetchCart();
          }
        }
      } catch (err) {
        console.error('[free-gift] reconcile error', err);
      } finally {
        // Always refresh UI from authoritative server state at the end of reconcile.
        // This defeats the race where cart-items.js renders stale section HTML
        // after our mutation finished — without this, removed gifts reappear in
        // the DOM until F5. Cost: one extra GET per reconcile (acceptable).
        try {
          if (finalCart) await this.refreshCartUIs(finalCart);
        } catch (err) {
          console.warn('[free-gift] final refresh failed', err);
        }
        this.busy = false;
        if (this._pending) {
          this._pending = false;
          this.scheduleReconcile();
        }
      }
    }

    notifyChooserUI() {
      try {
        // Cache last pending so a late-mounted chooser (e.g. drawer just opened
        // or sections re-rendered after AJAX) can re-request it via
        // window.freeGiftEngine.notifyChooserUI() (Bug 4 hardening).
        this._lastPending = this.pendingChoices || [];
        document.dispatchEvent(new CustomEvent('free-gift:pending-update', {
          detail: { pending: this._lastPending, ui: this.ui || {} },
        }));
      } catch (_) {}
    }

    async addChosenGift(ruleId, variantId, quantity) {
      ruleId = String(ruleId);
      variantId = parseInt(variantId, 10);
      quantity = parseInt(quantity, 10) || 1;
      if (!variantId) throw new Error('variantId required');
      try {
        await this.addGift(variantId, quantity, ruleId);
        const fresh = await this.fetchCart();
        await this.refreshCartUIs(fresh);
        // Re-evaluate to clear pending state.
        this.evaluate(fresh);
        this.notifyChooserUI();
        return true;
      } catch (err) {
        console.error('[free-gift] addChosenGift failed', err);
        throw err;
      }
    }

    scheduleReconcile() {
      if (this.evalTimer) clearTimeout(this.evalTimer);
      this.evalTimer = setTimeout(() => {
        this.evalTimer = null;
        this.reconcile();
      }, RECONCILE_DEBOUNCE_MS);
    }

    /**
     * Wraps window.fetch so any call hitting /cart/add.js, /cart/change.js,
     * /cart/update.js or /cart/clear.js triggers a reconcile after the response
     * resolves. Catches all cart mutations regardless of which UI triggered them
     * (theme PubSub, third-party widgets, raw fetch from cross-sell, etc).
     * Skips internal calls tagged with our SOURCE_TAG header.
     */
    installFetchInterceptor() {
      if (window.__freeGiftFetchPatched) return;
      window.__freeGiftFetchPatched = true;
      const originalFetch = window.fetch.bind(window);
      // QA#7: anchor the path so third-party trackers like
      // analytics.example.com/cart/add-pixel.gif don't match.
      const cartMutationRe = /^(?:\/|https?:\/\/[^/]+\/)cart\/(add|change|update|clear)(?:\.js|\b)/i;
      const sameOriginRe = new RegExp('^https?://' + location.host.replace(/[.+?^${}()|[\\]\\\\]/g, '\\$&'));
      const self = this;
      window.fetch = function (input, init) {
        const url = typeof input === 'string'
          ? input
          : (input && input.url) || '';
        const isSameOrigin = !url || url.startsWith('/') || sameOriginRe.test(url);
        const isCartMutation = isSameOrigin && cartMutationRe.test(url);
        // Headers can arrive as plain object, Headers instance, or [[k,v]] array.
        // Cover all three so we don't accidentally re-trigger reconcile on our own
        // internal mutations (would cause infinite recursion under bad timing).
        // QA#3: also pull headers off Request instances when init is missing.
        let hdrs = init && init.headers;
        if (!hdrs && input && typeof input === 'object' && input.headers) {
          hdrs = input.headers;
        }
        let sourceVal = null;
        if (hdrs) {
          if (typeof Headers !== 'undefined' && hdrs instanceof Headers) {
            sourceVal = hdrs.get('X-Free-Gift-Source');
          } else if (Array.isArray(hdrs)) {
            const found = hdrs.find((p) => Array.isArray(p) && p[0] && p[0].toLowerCase() === 'x-free-gift-source');
            sourceVal = found ? found[1] : null;
          } else if (typeof hdrs === 'object') {
            sourceVal = hdrs['X-Free-Gift-Source'] || hdrs['x-free-gift-source'] || null;
          }
        }
        // QA#3: any tagged caller is its own renderer — accept any non-null
        // source as "internal", not just SOURCE_TAG. Prevents the engine from
        // reconciling on top of the coupon IIFE's own /cart/update.js call.
        const isInternal = sourceVal != null && sourceVal !== '';
        const promise = originalFetch(input, init);
        if (isCartMutation && !isInternal) {
          promise.then((res) => {
            if (res && res.ok) self.scheduleReconcile();
          }).catch(() => {});
        }
        return promise;
      };
    }

    init() {
      if (!this.loadConfig()) return;

      this.installFetchInterceptor();

      if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
        this.cartUpdateUnsubscribe = window.subscribe(
          window.PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event && event.source === SOURCE_TAG) return;
            this.scheduleReconcile();
          }
        );
      }
      // Also listen to common DOM events fired by various cart UIs.
      // QA#4: skip events we dispatched ourselves — refreshCartUIs fires
      // 'cart:updated' with detail.source = SOURCE_TAG, and without this
      // guard every reconcile self-triggers another (saved only by the
      // token cache short-circuit, but still doubles work).
      ['cart:updated', 'cart:refresh', 'cart:change', 'cart-drawer:updated'].forEach((evt) => {
        document.addEventListener(evt, (e) => {
          if (e && e.detail && e.detail.source === SOURCE_TAG) return;
          this.scheduleReconcile();
        });
      });

      this.scheduleReconcile();
    }
  }

  function start() {
    const engine = new FreeGiftEngine();
    window.freeGiftEngine = engine;
    engine.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
